import {arrayToCsv} from '@netx/core-next/utils/array';
import {dateSubTypes1_default} from '@netx/core-search/properties/search';
import {
	DEFAULT_SEARCH_SORT_FIELD,
	DEFAULT_SEARCH_SORT_ORDER,
	SALO_STRING_MAP,
} from '../constants';
import {SearchType} from '../rules/constants';
import {CheckoutType} from '../rules/checkouts/constants';
import {DateType, DateSubType} from '../rules/date/constants';
import {metadataNameHackOut} from '../rules/metadata/utils';
import type {SearchCriteria, SmallSALO} from '../types';

/**
 * Handles quirks in how data was presented to the user vs sent to the server\
 * This is to support old advanced search urls that may have been bookmarked
 * @note this is no longer used by the advanced search editor
 * @param searchCriteria
 * @returns
 */
export const correctSearchCriteriaQuirks = (searchCriteria: SearchCriteria) => {
	// quirks from our editor
	// TODO: this should be unnecessary once we can tackle that guy
	searchCriteria.elementTypes.forEach((elementType, index) => {
		switch (elementType) {
			case SearchType.METADATA: {
				searchCriteria.elementValue2s[index] = metadataNameHackOut(
					searchCriteria.elementValue2s[index].toString(),
				);
				break;
			}
			case SearchType.DATE: {
				// DAM-16349 - in-between date search rules needs adjusting (and refactoring)
				// so it does not need the below date hacking
				// (even grosser) Do not apply this hack if it was a system defined date
				if (
					searchCriteria.elementSubType2s[index] === DateSubType.IN_BETWEEN &&
					// Not user defined date attributes
					[
						DateType.CREATION_DATE,
						DateType.IMPORT_DATE,
						DateType.LAST_MODIFIED,
					].includes(searchCriteria.elementSubType1s[index])
				) {
					// assume already corrected
					if (searchCriteria.elementValue3s[index] === '') break;
					// toDate goes in value2 (editor puts it in value3)
					searchCriteria.elementValue2s[index] =
						searchCriteria.elementValue3s[index];
					// empty out what the editor was claiming to be value3 (which is only used for user defined attributes)
					searchCriteria.elementValue3s[index] = '';
				}
				// DEVNOTE: obscure corner case bug here.
				// .. then it's an attribute Date search.
				// In the UI, Date attributes automagically show up on the 'date' search type UI, and have subtype ids other than the standard set of them.
				// But these searches are encoded differently.
				// In order for this search to work, elementSubType1 can not reference the option in the dropdown but needs to use the value of 2 to signify that we want to use a
				// date attribute - and the value we have chosen via elementSubType1 becomes the value for elementValue3
				// Therefore: change elementSubType1 to 2 and elementValue3 to the name of the attr, which is the elementSubType1 value
				// This will not run if already corrected since the elementSubType1s _should_ no longer match a system defined attribute
				// TODO:
				else if (
					!dateSubTypes1_default.find(
						(item) => item.val === searchCriteria.elementSubType1s[index],
					) &&
					searchCriteria.elementSubType1s[index] !==
						DateType.USER_DEFINED_ATTRIBUTE
				) {
					// value2 is actually the value of the attribute
					searchCriteria.elementValue2s[index] =
						searchCriteria.elementValue3s[index];
					// subType1 is actually our attribute name
					searchCriteria.elementValue3s[index] =
						searchCriteria.elementSubType1s[index];
					// tell it that it is a custom type
					searchCriteria.elementSubType1s[index] =
						DateType.USER_DEFINED_ATTRIBUTE;
				}
				break;
			}
			case SearchType.CHECKOUTS: {
				// if all is selected, java seems to want user id to be 0
				if (searchCriteria.elementSubType1s[index] === CheckoutType.ALL) {
					// TODO: does it want zero here to say no user - or is it wanting us to signify
					// CheckoutSubType1.ALL again explicitly
					// I know it really does not make a difference right now... but would be good to
					// tie it to a enum if it is...
					searchCriteria.elementValue1s[index] = 0;
					break;
				}
			}
		}
	});

	return searchCriteria;
};

/**
 * Helper for converting condensed search criteria to full search criteria object
 * @param smallSALO
 * @returns Search criteria
 */
export const getSearchCriteriaFromSmallSALO = (smallSALO: SmallSALO) => {
	const searchCriteria = Object.entries(SALO_STRING_MAP).reduce<SearchCriteria>(
		(accum, [key, realName]) => {
			let value = smallSALO[key as keyof typeof SALO_STRING_MAP];
			if (value == null) return accum;
			switch (realName) {
				case 'elementValue1s':
				case 'elementValue2s':
				case 'elementValue3s':
					// TODO: is this actually neccessary - we should not have arrays for values
					// given how we break everything up before submitting search rules
					value = (value as Array<string | number>).map((val) =>
						Array.isArray(val) ? arrayToCsv(val) : val,
					);
					break;
			}
			accum[realName] = value as unknown as never;
			return accum;
		},
		{
			sortField: DEFAULT_SEARCH_SORT_FIELD,
			sortOrder: DEFAULT_SEARCH_SORT_ORDER,
			matchCriteria: 0,
			elementTypes: [],
			elementSubType1s: [],
			elementSubType2s: [],
			elementValue1s: [],
			elementValue2s: [],
			elementValue3s: [],
		},
	);

	return correctSearchCriteriaQuirks(searchCriteria);
};

export default getSearchCriteriaFromSmallSALO;
